import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Printer, ArrowRightLeft, CheckCircle2, Eye, Pencil, Trash2, CopyPlus } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "../utils";
import api from "../api";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import DocumentPreviewDialog from "../components/print/DocumentPreviewDialog";

const statusColor = {
  Sent: "bg-slate-100 text-slate-600",
  "Under Evaluation": "bg-amber-50 text-amber-700",
  "Order Received": "bg-emerald-50 text-emerald-700",
  "Converted to Bill": "bg-teal-50 text-teal-700",
  Rejected: "bg-rose-50 text-rose-700",
};

export default function Quotations() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [orderModal, setOrderModal] = useState(null);
  const [orderNo, setOrderNo] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const { t } = useLang();
  const nav = useNavigate();

  const load = async () => setRows((await api.get("/quotations")).data);
  useEffect(() => { load().catch(() => {}); }, []);

  const markOrder = async () => {
    if (!orderNo.trim()) return toast.error("Order / PO number enter karo");
    if (!orderDate) return toast.error("Order date select karo");
    try {
      await api.put(`/quotations/${orderModal.id}/status`, null, {
        params: { status: "Order Received", order_no: orderNo.trim(), order_date: orderDate },
      });
      toast.success("Order Received — ab bill mein convert kar sakte ho");
      setOrderModal(null); setOrderNo(""); load();
    } catch (err) { toast.error(err.message); }
  };

  const removeQuotation = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quote_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      toast.success(`Quotation ${q.quote_no} deleted`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Quotation delete nahi ho paaya");
    }
  };

  return (
    <div data-testid="quotations-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("quotations")}</h1>
          <p className="sgp-page-subtitle">Simple price quotations — convert accepted quotes into sales bills.</p>
        </div>
        <Link to="/quotations/new" data-testid="new-quotation-btn"
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> {t("newQuotation")}
        </Link>
      </div>
      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr>
              <th className="sgp-th">Quote No.</th><th className="sgp-th">Date</th><th className="sgp-th">Hospital</th>
              <th className="sgp-th">Tender / Enquiry Ref.</th>
              <th className="sgp-th">Valid Till</th><th className="sgp-th text-right">Total</th>
              <th className="sgp-th">Status</th><th className="sgp-th">Order / PO</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="quotations-table">
            {rows.map((q) => (
              <tr key={q.id} data-testid={`quotation-row-${q.id}`} className="sgp-table-row">
                <td className="sgp-td font-mono text-xs font-semibold">{q.quote_no}</td>
                <td className="sgp-td font-mono text-xs">{q.date}</td>
                <td className="sgp-td">{q.hospital_name}</td>
                <td className="sgp-td font-mono text-xs">{q.tender_ref || q.letter_ref || "-"}</td>
                <td className="sgp-td font-mono text-xs">{q.valid_till || "-"}</td>
                <td className="sgp-td text-right font-mono">{fmt(q.grand_total)}</td>
                <td className="sgp-td"><span data-testid={`quotation-status-${q.id}`} className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColor[q.status] || statusColor.Sent}`}>{q.status}</span></td>
                <td className="sgp-td font-mono text-xs">{q.order_no ? `${q.order_no} (${q.order_date})` : "-"}</td>
                <td className="sgp-td">
                  <div className="flex gap-1 items-center">
                    <button data-testid={`preview-quotation-${q.id}`} type="button" onClick={() => setPreview({ kind: "quotation", id: q.id, label: `Rate Quotation ${q.quote_no}` })} title="Preview"
                      className="p-1.5 rounded hover:bg-blue-50 text-slate-500 hover:text-blue-700"><Eye className="h-4 w-4" /></button>
                    <Link data-testid={`print-quotation-${q.id}`} to={`/print/quotation/${q.id}`} target="_blank" rel="noreferrer" title="Print / PDF"
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700"><Printer className="h-4 w-4" /></Link>
                    {q.status !== "Converted to Bill" && (
                      <Link data-testid={`edit-quotation-${q.id}`} to={`/quotations/${q.id}/edit`} title="Edit"
                        className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700"><Pencil className="h-4 w-4" /></Link>
                    )}
                    <Link data-testid={`duplicate-quotation-${q.id}`} to={`/quotations/new?copy_from=${q.id}`} title="Duplicate as new quotation"
                      className="p-1.5 rounded hover:bg-violet-50 text-slate-500 hover:text-violet-700"><CopyPlus className="h-4 w-4" /></Link>
                    <button data-testid={`delete-quotation-${q.id}`} type="button" title="Delete"
                      onClick={() => removeQuotation(q)}
                      className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                    {q.status === "Sent" && (
                      <button data-testid={`order-received-${q.id}`} title="Mark Order Received"
                        onClick={() => setOrderModal(q)}
                        className="p-1.5 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700"><CheckCircle2 className="h-4 w-4" /></button>
                    )}
                    {q.status === "Order Received" && (
                      <button data-testid={`convert-to-bill-${q.id}`} title="Convert to Sales Bill"
                        onClick={() => nav(`/sales/new?from_quotation=${q.id}`)}
                        className="p-1.5 rounded hover:bg-teal-50 text-slate-500 hover:text-teal-700"><ArrowRightLeft className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="sgp-td text-center py-10 text-slate-400">Abhi koi quotation nahi.</td></tr>}
          </tbody>
        </table>
      </div>

      <DocumentPreviewDialog open={!!preview} onClose={() => setPreview(null)} kind={preview?.kind} id={preview?.id} label={preview?.label} />

      <Dialog open={!!orderModal} onOpenChange={() => setOrderModal(null)}>
        <DialogContent data-testid="order-received-modal" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Order / PO Details — {orderModal?.quote_no}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="sgp-label">Order / PO Number</label>
              <input data-testid="order-no-input" className="sgp-input font-mono" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} /></div>
            <div><label className="sgp-label">Order Date</label>
              <input data-testid="order-date-input" type="date" className="sgp-input font-mono" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} /></div>
            <button data-testid="order-confirm-btn" onClick={markOrder}
              className="w-full py-2 text-sm font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700">Mark Order Received</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
